import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { Plus, Edit, Trash2, UserCircle, Shield, Upload, Download, CheckCircle2, XCircle, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const AdminManagers = () => {
  const [managers, setManagers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [editingManager, setEditingManager] = useState(null);
  const [formData, setFormData] = useState({
    email: "", name: "", password: "", team_id: ""
  });
  const [csvRows, setCsvRows] = useState([]);
  const [bulkResults, setBulkResults] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [csvFileName, setCsvFileName] = useState("");

  const fetchData = async () => {
    try {
      const [managersRes, teamsRes] = await Promise.all([
        axios.get(`${API}/managers`, { withCredentials: true }),
        axios.get(`${API}/teams`)
      ]);
      setManagers(managersRes.data);
      setTeams(teamsRes.data);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const teamsMap = {};
  teams.forEach(t => { teamsMap[t.team_id] = t; });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingManager) {
        const updateData = {
          name: formData.name,
          team_id: formData.team_id
        };
        if (formData.password) updateData.password = formData.password;
        await axios.put(`${API}/managers/${editingManager.user_id}`, updateData, { withCredentials: true });
        toast.success("Manager updated");
      } else {
        await axios.post(`${API}/managers`, formData, { withCredentials: true });
        toast.success("Manager account created");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save manager");
    }
  };

  const handleEdit = (manager) => {
    setEditingManager(manager);
    setFormData({
      email: manager.email,
      name: manager.name,
      password: "",
      team_id: manager.team_id
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (userId, email) => {
    if (!window.confirm(`Delete manager account for ${email}?`)) return;
    try {
      await axios.delete(`${API}/managers/${userId}`, { withCredentials: true });
      toast.success("Manager deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete manager");
    }
  };

  const resetForm = () => {
    setEditingManager(null);
    setFormData({ email: "", name: "", password: "", team_id: "" });
  };

  // Teams that don't have a manager yet (for the create form)
  const managerTeamIds = new Set(managers.map(m => m.team_id));
  const availableTeams = editingManager 
    ? teams // when editing, show all so current team stays in options
    : teams.filter(t => !managerTeamIds.has(t.team_id));

  // CSV parsing (simple, handles quoted values with commas)
  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length === 0) return [];
    
    const parseRow = (line) => {
      const cells = [];
      let current = "";
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
          if (inQuotes && line[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (ch === ',' && !inQuotes) {
          cells.push(current.trim());
          current = "";
        } else {
          current += ch;
        }
      }
      cells.push(current.trim());
      return cells;
    };
    
    const headers = parseRow(lines[0]).map(h => h.toLowerCase().trim());
    return lines.slice(1).map(line => {
      const cells = parseRow(line);
      const row = {};
      headers.forEach((h, i) => { row[h] = cells[i] || ""; });
      return row;
    });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFileName(file.name);
    setBulkResults(null);
    
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const text = evt.target.result;
        const rows = parseCSV(text);
        setCsvRows(rows);
        if (rows.length === 0) {
          toast.error("CSV file is empty");
        } else {
          toast.success(`Parsed ${rows.length} rows`);
        }
      } catch (err) {
        toast.error("Failed to parse CSV file");
      }
    };
    reader.readAsText(file);
  };

  const handleBulkImport = async () => {
    if (csvRows.length === 0) {
      toast.error("No rows to import");
      return;
    }
    
    setBulkLoading(true);
    try {
      const managers_payload = csvRows.map(row => ({
        email: row.email || "",
        name: row.name || "",
        password: row.password || "",
        team_name: row.team_name || row["team name"] || null,
        team_short_name: row.team_short_name || row["team short name"] || row.short_name || null,
      }));
      
      const { data } = await axios.post(
        `${API}/managers/bulk`,
        { managers: managers_payload },
        { withCredentials: true }
      );
      setBulkResults(data);
      
      if (data.created.length > 0) {
        toast.success(`Created ${data.created.length} manager account${data.created.length === 1 ? '' : 's'}`);
        fetchData();
      }
      if (data.errors.length > 0) {
        toast.warning(`${data.errors.length} row${data.errors.length === 1 ? '' : 's'} had errors`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bulk import failed");
    } finally {
      setBulkLoading(false);
    }
  };

  const downloadTemplate = () => {
    const csv = "email,name,password,team_short_name\nasu.manager@example.com,ASU Manager,ChangeMe123,ASU\ngrn.manager@example.com,GRN Manager,ChangeMe123,GRN\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "manager_import_template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Template downloaded");
  };

  const closeBulkDialog = () => {
    setIsBulkOpen(false);
    setCsvRows([]);
    setBulkResults(null);
    setCsvFileName("");
  };

  return (
    <AdminLayout title="Team Managers">
      <div data-testid="admin-managers">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <p className="text-gray-500">Create login accounts for team managers</p>
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={() => setIsBulkOpen(true)}
              className="rounded-none border-[#09090b] hover:bg-[#09090b] hover:text-white"
              data-testid="bulk-import-btn"
            >
              <Upload className="w-4 h-4 mr-2" /> Bulk Import
            </Button>
            <Dialog open={isDialogOpen} onOpenChange={(o) => { setIsDialogOpen(o); if (!o) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#FFC72C] hover:bg-[#E6B325] text-black rounded-none" data-testid="add-manager-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Manager
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-heading font-bold uppercase">
                    {editingManager ? "Edit Manager" : "Create Team Manager"}
                  </DialogTitle>
                  <DialogDescription>
                    {editingManager ? "Update manager account details." : "Create a new team manager account with team assignment."}
                  </DialogDescription>
                </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="manager-form">
                <div>
                  <label className="text-sm font-medium">Manager Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required className="rounded-none" data-testid="manager-name-input"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Email *</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    disabled={!!editingManager}
                    className="rounded-none disabled:opacity-50"
                    data-testid="manager-email-input"
                    placeholder="manager@team.com"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">
                    Password {editingManager && <span className="text-xs text-gray-500">(leave blank to keep current)</span>}
                    {!editingManager && ' *'}
                  </label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required={!editingManager}
                    minLength={6}
                    className="rounded-none"
                    data-testid="manager-password-input"
                    placeholder="Min 6 characters"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Assign to Team *</label>
                  <Select value={formData.team_id} onValueChange={(v) => setFormData({...formData, team_id: v})}>
                    <SelectTrigger className="rounded-none" data-testid="manager-team-select">
                      <SelectValue placeholder="Select Team" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTeams.length === 0 ? (
                        <div className="p-2 text-sm text-gray-500">All teams have managers</div>
                      ) : (
                        availableTeams.map(team => (
                          <SelectItem key={team.team_id} value={team.team_id}>{team.name}</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-4">
                  <Button type="submit" className="flex-1 bg-[#FFC72C] hover:bg-[#E6B325] text-black rounded-none" data-testid="manager-submit-btn">
                    {editingManager ? "Update Manager" : "Create Manager"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-none">
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Bulk Import Dialog */}
        <Dialog open={isBulkOpen} onOpenChange={(o) => { if (!o) closeBulkDialog(); else setIsBulkOpen(true); }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold uppercase">Bulk Import Managers</DialogTitle>
              <DialogDescription>
                Upload a CSV file to create multiple manager accounts at once. Required columns: email, name, password, and either team_short_name or team_name.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6" data-testid="bulk-import-dialog">
              {/* Step 1: Template Download */}
              <div className="p-4 bg-gray-50 border border-gray-200">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <h4 className="font-heading font-bold text-sm uppercase mb-1">Step 1: Download Template</h4>
                    <p className="text-xs text-gray-500">Get the CSV template with required columns.</p>
                  </div>
                  <Button
                    onClick={downloadTemplate}
                    variant="outline"
                    size="sm"
                    className="rounded-none"
                    data-testid="download-template-btn"
                  >
                    <Download className="w-4 h-4 mr-2" /> Template
                  </Button>
                </div>
              </div>

              {/* Step 2: File Upload */}
              <div className="p-4 border-2 border-dashed border-gray-300">
                <h4 className="font-heading font-bold text-sm uppercase mb-3">Step 2: Upload CSV File</h4>
                <label className="flex flex-col items-center justify-center gap-3 py-8 cursor-pointer hover:bg-gray-50 transition-colors" data-testid="csv-upload-label">
                  <Upload className="w-10 h-10 text-gray-400" />
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-700">
                      {csvFileName ? csvFileName : "Click to upload CSV file"}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">CSV files only (max 200 rows)</p>
                  </div>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="csv-file-input"
                  />
                </label>
              </div>

              {/* Step 3: Preview */}
              {csvRows.length > 0 && !bulkResults && (
                <div>
                  <h4 className="font-heading font-bold text-sm uppercase mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Step 3: Preview ({csvRows.length} rows)
                  </h4>
                  <div className="max-h-64 overflow-y-auto border border-gray-200">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="text-left p-2 w-10">#</th>
                          <th className="text-left p-2">Email</th>
                          <th className="text-left p-2">Name</th>
                          <th className="text-left p-2">Team</th>
                        </tr>
                      </thead>
                      <tbody>
                        {csvRows.slice(0, 20).map((row, i) => (
                          <tr key={i} className="border-t border-gray-100">
                            <td className="p-2 text-gray-400">{i + 1}</td>
                            <td className="p-2 truncate max-w-xs">{row.email || <span className="text-red-500">missing</span>}</td>
                            <td className="p-2 truncate max-w-xs">{row.name || <span className="text-red-500">missing</span>}</td>
                            <td className="p-2 truncate max-w-xs">
                              {row.team_short_name || row.team_name || <span className="text-red-500">missing</span>}
                            </td>
                          </tr>
                        ))}
                        {csvRows.length > 20 && (
                          <tr>
                            <td colSpan="4" className="p-2 text-center text-gray-500 italic">
                              ...and {csvRows.length - 20} more rows
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Results */}
              {bulkResults && (
                <div data-testid="bulk-results">
                  <h4 className="font-heading font-bold text-sm uppercase mb-3">Import Results</h4>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="p-4 bg-green-50 border-l-4 border-[#009E60]">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle2 className="w-5 h-5 text-[#009E60]" />
                        <span className="font-heading font-bold text-2xl">{bulkResults.created.length}</span>
                      </div>
                      <p className="text-xs text-gray-600">Successfully created</p>
                    </div>
                    <div className="p-4 bg-red-50 border-l-4 border-red-500">
                      <div className="flex items-center gap-2 mb-1">
                        <XCircle className="w-5 h-5 text-red-500" />
                        <span className="font-heading font-bold text-2xl">{bulkResults.errors.length}</span>
                      </div>
                      <p className="text-xs text-gray-600">Errors</p>
                    </div>
                  </div>

                  {bulkResults.errors.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-bold text-red-700 mb-2">Errors:</p>
                      <div className="max-h-40 overflow-y-auto border border-red-200 bg-red-50 p-2 text-xs space-y-1">
                        {bulkResults.errors.map((err, i) => (
                          <div key={i} className="flex gap-2">
                            <span className="text-gray-500">Row {err.row}:</span>
                            <span className="font-medium">{err.email}</span>
                            <span className="text-red-600">— {err.error}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {bulkResults.created.length > 0 && (
                    <div>
                      <p className="text-sm font-bold text-[#009E60] mb-2">Created accounts:</p>
                      <div className="max-h-40 overflow-y-auto border border-green-200 bg-green-50 p-2 text-xs space-y-1">
                        {bulkResults.created.map((c, i) => (
                          <div key={i} className="flex gap-2">
                            <span className="text-gray-500">Row {c.row}:</span>
                            <span className="font-medium">{c.email}</span>
                            <span className="text-gray-500">→ {c.team_name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2 border-t border-gray-200">
                {!bulkResults ? (
                  <>
                    <Button
                      onClick={handleBulkImport}
                      disabled={csvRows.length === 0 || bulkLoading}
                      className="flex-1 bg-[#FFC72C] hover:bg-[#E6B325] text-black rounded-none disabled:opacity-50"
                      data-testid="import-csv-btn"
                    >
                      {bulkLoading ? "Importing..." : `Import ${csvRows.length} Rows`}
                    </Button>
                    <Button
                      onClick={closeBulkDialog}
                      variant="outline"
                      className="rounded-none"
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={closeBulkDialog}
                    className="w-full bg-[#009E60] hover:bg-[#008050] text-white rounded-none"
                    data-testid="close-bulk-results-btn"
                  >
                    Done
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : managers.length > 0 ? (
          <div className="space-y-3">
            {managers.map(m => (
              <div key={m.user_id} className="bg-white border border-gray-200 p-4 flex items-center gap-4 group" data-testid={`manager-card-${m.user_id}`}>
                <div className="w-12 h-12 bg-[#FFC72C] flex items-center justify-center">
                  <Shield className="w-6 h-6 text-black" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-bold text-lg uppercase text-[#09090b]">{m.name}</h3>
                  <p className="text-sm text-gray-500 truncate">{m.email}</p>
                </div>
                <div className="hidden md:flex items-center gap-3">
                  <img 
                    src={teamsMap[m.team_id]?.logo_url || 'https://placehold.co/40x40/09090b/FFF?text=T'}
                    alt=""
                    className="w-10 h-10 object-contain"
                  />
                  <div>
                    <p className="font-medium text-sm">{teamsMap[m.team_id]?.name || 'Unknown Team'}</p>
                    <p className="text-xs text-gray-500">{teamsMap[m.team_id]?.short_name}</p>
                  </div>
                </div>
                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button size="sm" variant="outline" onClick={() => handleEdit(m)} className="rounded-none" data-testid={`edit-manager-${m.user_id}`}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(m.user_id, m.email)} className="rounded-none" data-testid={`delete-manager-${m.user_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-white border border-gray-200">
            <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl text-gray-400 uppercase mb-2">No Managers Yet</h3>
            <p className="text-gray-500 mb-4">Create manager accounts to let team managers update their own team data.</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminManagers;
